import { Suspense, lazy } from "react";
import { Navigate, Outlet, useRoutes } from "react-router-dom";
import NotFoundPage from "../../features/error/NotFoundPage";
import RedirectIfAuthenticated from "../../components/RedirectIfAuthenticated";
import ProtectedRoute from "../../components/ProtectedRoute";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/PageLoader";

const LoginPage = lazy(() => import("../../features/auth/pages/Login"));
const SignUpPage = lazy(() => import("../../features/auth/pages/SignUp"));
const ForgotPasswordPage = lazy(
  () => import("../../features/auth/pages/ForgotPassword"),
);
const OAuthCallbackPage = lazy(
  () => import("../../features/auth/pages/OAuthCallback"),
);
const HomePage = lazy(() => import("../../features/home/pages/HomePage"));
const SettingsLayout = lazy(
  () => import("../../features/settings/pages/SettingsLayout"),
);
const ProfilePage = lazy(
  () => import("../../features/settings/pages/ProfilePage"),
);
const AddressesPage = lazy(
  () => import("../../features/settings/pages/AddressesPage"),
);
const PaymentMethodsPage = lazy(
  () => import("../../features/settings/pages/PaymentMethodsPage"),
);
const SecurityPage = lazy(
  () => import("../../features/settings/pages/SecurityPage"),
);
const SettingsIndex = lazy(
  () => import("../../features/settings/pages/SettingsIndex"),
);
const BookingPage = lazy(
  () => import("../../features/booking/pages/BookingPage"),
);
const MyBookingsPage = lazy(
  () => import("../../features/bookings/pages/MyBookingsPage"),
);
const BookingDetailPage = lazy(
  () => import("../../features/bookings/pages/BookingDetailPage"),
);
const AllServicesPage = lazy(
  () => import("../../features/categories/pages/ServicesPage"),
);
const TermsOfServicePage = lazy(
  () => import("../../features/legal/pages/TermsOfServicePage"),
);
const PrivacyPolicyPage = lazy(
  () => import("../../features/legal/pages/PrivacyPolicyPage"),
);
const AboutPage = lazy(() => import("../../features/about/pages/AboutPage"));
const CareersPage = lazy(
  () => import("../../features/careers/pages/CareersPage"),
);

const HomeownerApp = () => {
  const appRoutes = [
    {
      path: "/",
      element: (
        <MainLayout>
          <Outlet />
        </MainLayout>
      ),
      children: [
        {
          element: <HomePage />,
          index: true,
        },
        {
          path: "book/:serviceName",
          element: <BookingPage />,
        },
        {
          path: "services",
          element: <AllServicesPage />,
        },
        {
          path: "about",
          element: <AboutPage />,
        },
        {
          path: "careers",
          element: <CareersPage />,
        },
        {
          path: "terms",
          element: <TermsOfServicePage />,
        },
        {
          path: "privacy",
          element: <PrivacyPolicyPage />,
        },
        // Protected routes
        {
          path: "bookings",
          element: (
            <ProtectedRoute>
              <MyBookingsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: "bookings/:bookingId",
          element: (
            <ProtectedRoute>
              <BookingDetailPage />
            </ProtectedRoute>
          ),
        },
      ],
    },
    // Settings routes - NOT nested under MainLayout
    {
      path: "settings",
      element: (
        <ProtectedRoute>
          <SettingsLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <SettingsIndex />,
        },
        {
          path: "profile",
          element: <ProfilePage />,
        },
        {
          path: "addresses",
          element: <AddressesPage />,
        },
        {
          path: "payment-methods",
          element: <PaymentMethodsPage />,
        },
        {
          path: "security",
          element: <SecurityPage />,
        },
      ],
    },
    // Auth routes
    {
      path: "auth/login",
      element: (
        <RedirectIfAuthenticated>
          <LoginPage />
        </RedirectIfAuthenticated>
      ),
    },
    {
      path: "auth/signup",
      element: (
        <RedirectIfAuthenticated>
          <SignUpPage />
        </RedirectIfAuthenticated>
      ),
    },
    {
      path: "auth/forgot-password",
      element: (
        <RedirectIfAuthenticated>
          <ForgotPasswordPage />
        </RedirectIfAuthenticated>
      ),
    },
    // OAuth callback route
    {
      path: "oauth/callback",
      element: <OAuthCallbackPage />,
    },
    {
      path: "/404",
      element: <NotFoundPage />,
    },
    {
      path: "*",
      element: <Navigate to="/404" replace />,
    },
  ];

  return useRoutes(appRoutes);
};

export default function HomeownerAppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <HomeownerApp />
    </Suspense>
  );
}
